import type {
  CoreSectionProps,
  FlattenedSectionProps,
} from "../../../../../components/Questionnaire/Section";
import type { VisibilityMap } from "../../../../../components/Questionnaire/validation/form/types";
import type { Answers, Groups } from "../../../types";
import type {
  PartialQuestionOrGroup,
  QuestionProps,
} from "../../../../../components/Questionnaire/Section/Question/types";
import {
  isQuestion,
  isAddressLookupGroup,
  isMultiselectGroup,
  isMultiselectToggleGroup,
  isPercentGroup,
  isYesNoToggleGroup,
  isPersonalisedCoverQuestionGroup,
  isPersonalisedCoverShadowGroup,
  isAgreementType,
} from "../../../../../components/Questionnaire/Section/Question/types";
import { flattenYesNoToggleGroup } from "./yesNoToggle/yesNoToggleGroup";
import { flattenAgreementGroup } from "./agreementGroup/agreementGroup";
import { flattenMultiselectGroup } from "./multiselect/multiselectGroup";
import { flattenMultiselectToggleGroup } from "./multiselectToggle/multiselectToggleGroup";
import { flattenAddressLookupGroup } from "./addressLookup/addressLookupGroup";
import { flattenPercentGroup } from "./percentGroup/percentGroup";
import {
  flattenPersonalisedCoverGroup,
  flattenPersonalisedCoverShadowGroup,
} from "./personalisedCoverGroup/personalisedCoverGroup";
import { createEmptyFlattenResult } from "./utils";

type SectionWithQuestionGroups = CoreSectionProps & {
  questions: PartialQuestionOrGroup[];
};

type FlattenSectionsParams<S extends SectionWithQuestionGroups> = {
  sections: S[];
  answers: Answers;
  visibility?: VisibilityMap;
};

type FlattenSectionsResult = {
  sections: FlattenedSectionProps[];
  answers: Answers;
  groups: Groups;
  visibility: VisibilityMap;
  groupQuestionNames: Set<string>;
};

/**
 * Flattens sections by converting nested question groups into flat question lists.
 * Processes each section and transforms question groups based on their type.
 */
export const flattenSections = <S extends SectionWithQuestionGroups>({
  sections,
  answers: initialAnswers,
  visibility: initialVisibility = {},
}: FlattenSectionsParams<S>): FlattenSectionsResult => {
  const answers = { ...initialAnswers };
  const visibility: VisibilityMap = { ...initialVisibility };
  const groups: Groups = {};

  const flattenedSections: FlattenedSectionProps[] = sections.map(section => ({
    ...section,
    questions: section.questions.flatMap(question => {
      if (isQuestion(question)) {
        return [{ ...question, sectionId: section.id }];
      }

      let result;

      if (isYesNoToggleGroup(question)) {
        result = flattenYesNoToggleGroup(question, answers, visibility);
      } else if (isMultiselectGroup(question)) {
        result = flattenMultiselectGroup(question, answers, visibility);
      } else if (isMultiselectToggleGroup(question)) {
        result = flattenMultiselectToggleGroup(question, answers, visibility);
      } else if (isAddressLookupGroup(question)) {
        result = flattenAddressLookupGroup(question, answers, visibility);
      } else if (isPercentGroup(question)) {
        result = flattenPercentGroup(question, answers, visibility);
      } else if (isPersonalisedCoverShadowGroup(question)) {
        result = flattenPersonalisedCoverShadowGroup(
          question,
          answers,
          visibility,
        );
      } else if (isPersonalisedCoverQuestionGroup(question)) {
        result = flattenPersonalisedCoverGroup(question, answers, visibility);
      } else if (isAgreementType(question)) {
        result = flattenAgreementGroup(question, answers, visibility);
      } else {
        result = createEmptyFlattenResult(answers, visibility);
      }

      Object.assign(groups, result.groups);
      Object.assign(answers, result.answers);
      Object.assign(visibility, result.visibility);

      // Assign sectionId to all questions returned from group flattening
      return (result.questions as QuestionProps[]).map(q => ({
        ...q,
        sectionId: section.id,
      }));
    }),
  }));

  // Build Set of all question names in groups for O(1) lookup
  const groupQuestionNames = new Set<string>();
  for (const group of Object.values(groups)) {
    for (const name of group.names) {
      groupQuestionNames.add(name);
    }
  }

  return {
    sections: flattenedSections,
    answers,
    groups,
    visibility,
    groupQuestionNames,
  };
};
